import { getSvgMetrics } from './brandTheme'
import { getAxisGroupNode } from './axisNodes'

function toFiniteNumber(value) {
  return Number.isFinite(value) ? value : null
}

function getTickCenter(textNode) {
  const box = textNode.getBBox()
  return box.x + box.width / 2
}

function getMedian(values) {
  if (!values.length) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2
  }
  return sorted[middle]
}

function getBottomAxisGroup(svgNode, chartId = null) {
  const sharedXAxisGroup = getAxisGroupNode(svgNode, 'x', chartId)
  if (sharedXAxisGroup) return sharedXAxisGroup

  const candidates = Array.from(svgNode.querySelectorAll('g')).filter((node) => {
    const id = (node.getAttribute('id') || '').toLowerCase()
    return id === 'xaxis' || id === 'x axis' || id === 'x-axis'
  })

  if (!candidates.length) return null

  return candidates
    .map((node) => {
      try {
        const box = node.getBBox()
        return { node, bottom: box.y + box.height }
      } catch (error) {
        return null
      }
    })
    .filter(Boolean)
    .sort((a, b) => b.bottom - a.bottom)[0]?.node
}

function getTickTextNodes(axisGroup) {
  const explicitTickTexts = Array.from(axisGroup.querySelectorAll('.tick text'))
  if (explicitTickTexts.length) {
    return explicitTickTexts.filter((node) => {
      const content = node.textContent?.trim()
      return Boolean(content)
    })
  }

  const texts = Array.from(axisGroup.querySelectorAll('g > text'))
  return texts.filter((node) => {
    const content = node.textContent?.trim()
    if (!content) return false

    const parentGroup = node.parentElement
    if (!parentGroup) return false

    const parentClass = (parentGroup.getAttribute('class') || '').toLowerCase()
    if (parentClass.includes('label') || parentClass.includes('title')) return false

    const hasTickShape = Boolean(parentGroup.querySelector('line, path'))
    const hasTransform = parentGroup.hasAttribute('transform')

    return hasTickShape || hasTransform
  })
}

export function truncateAxisLabel(label, maxLength = 14) {
  const normalized = String(label ?? '').trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`
}

export function getAxisLabelStrategy({
  tickCount,
  spacing,
  maxLabelLength,
  maxLabelWidth,
}) {
  if (tickCount < 3 || !spacing) {
    return { rotate: 0, truncateTo: null, stagger: false, extraBottomPadding: 0 }
  }

  const densityRatio = maxLabelWidth / spacing

  if (densityRatio <= 1.0) {
    return { rotate: 0, truncateTo: null, stagger: false, extraBottomPadding: 0 }
  }

  if (densityRatio > 1.5) {
    return {
      rotate: -42,
      truncateTo: maxLabelLength >= 18 ? 12 : null,
      stagger: false,
      extraBottomPadding: 46,
    }
  }

  return {
    rotate: -28,
    truncateTo: maxLabelLength >= 18 ? 14 : null,
    stagger: false,
    extraBottomPadding: 28,
  }
}

export function getAxisTickTextNodes(axisGroup) {
  return getTickTextNodes(axisGroup)
}

function setTickTitle(textNode, fullText) {
  let titleNode = textNode.querySelector('title')
  if (!titleNode) {
    titleNode = document.createElementNS('http://www.w3.org/2000/svg', 'title')
    textNode.appendChild(titleNode)
  }
  titleNode.textContent = fullText
}

function removeTickTitle(textNode) {
  const titleNode = textNode.querySelector('title')
  if (titleNode) titleNode.remove()
}

function setTickLineLength(textNode, length) {
  const tickGroup = textNode.parentElement
  if (!tickGroup) return

  const tickLine = tickGroup.querySelector('line')
  if (!tickLine) return

  const y1 = Number(tickLine.getAttribute('y1') || 0)
  const y2 = Number(tickLine.getAttribute('y2') || 0)
  const x1 = Number(tickLine.getAttribute('x1') || 0)
  const x2 = Number(tickLine.getAttribute('x2') || 0)

  if (Number.isFinite(y1) && Number.isFinite(y2) && y1 !== y2) {
    const direction = y2 >= y1 ? 1 : -1
    tickLine.setAttribute('y2', String(y1 + direction * length))
    return
  }

  if (Number.isFinite(x1) && Number.isFinite(x2) && x1 !== x2) {
    const direction = x2 >= x1 ? 1 : -1
    tickLine.setAttribute('x2', String(x1 + direction * length))
  }
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function getManualAxisLabelStrategy(options = {}) {
  const strategy = options.labelOverflowStrategy || 'angled'
  const maxCharacters = clamp(Number(options.labelMaxCharacters) || 12, 6, 24)

  switch (strategy) {
    case 'horizontal':
      return { rotate: 0, truncateTo: null, stagger: false, extraBottomPadding: 0 }
    case 'stagger':
      return { rotate: 0, truncateTo: null, stagger: true, extraBottomPadding: 36 }
    case 'truncate':
      return { rotate: 0, truncateTo: maxCharacters, stagger: false, extraBottomPadding: 0 }
    case 'angled':
    default:
      return { rotate: -32, truncateTo: null, stagger: false, extraBottomPadding: 32 }
  }
}

function applyEveryNthVisibility(tickTexts, everyNth = 1) {
  const step = Math.max(1, Number(everyNth) || 1)
  tickTexts.forEach((textNode, index) => {
    const visible = step === 1 || index % step === 0
    textNode.style.display = visible ? '' : 'none'
  })
  return tickTexts.filter((_, index) => step === 1 || index % step === 0)
}

function ensureSvgBottomPadding(svgNode, requiredBottom) {
  if (!requiredBottom) return

  const metrics = getSvgMetrics(svgNode, {
    excludeSelectors: ['[data-lugsail-axis-guide-line="true"]'],
  })
  const nextHeight = Math.ceil(metrics.height + requiredBottom)

  if (metrics.hasViewBox) {
    svgNode.setAttribute(
      'viewBox',
      `${metrics.x} ${metrics.y} ${metrics.width} ${nextHeight}`
    )
  } else {
    svgNode.setAttribute('height', String(nextHeight))
  }

  const background = svgNode.querySelector('[data-lugsail-theme-background="true"]')
  if (background) {
    background.setAttribute('height', String(nextHeight))
  }
}

export function cleanCrowdedXAxisLabels(svgNode, options = {}) {
  if (!svgNode) return

  const axisGroup = getBottomAxisGroup(svgNode, options.chartId)
  if (!axisGroup) return

  const tickTexts = getTickTextNodes(axisGroup)
  if (tickTexts.length < 2) return

  const visibleTickTexts = applyEveryNthVisibility(tickTexts, options.labelShowEveryNth)
  if (visibleTickTexts.length < 2) return

  const centers = visibleTickTexts
    .map((node) => toFiniteNumber(getTickCenter(node)))
    .filter((value) => value !== null)
  const spacings = centers
    .slice(1)
    .map((center, index) => center - centers[index])
    .filter((value) => value > 0)

  const spacing = getMedian(spacings)
  const maxLabelLength = visibleTickTexts.reduce(
    (max, node) => Math.max(max, node.textContent.trim().length),
    0
  )
  const maxLabelWidth = visibleTickTexts.reduce((max, node) => {
    const box = node.getBBox()
    return Math.max(max, box.width)
  }, 0)

  const strategy =
    options.labelHandlingMode === 'manual'
      ? getManualAxisLabelStrategy(options)
      : visibleTickTexts.length < 4
        ? { rotate: 0, truncateTo: null, stagger: false, extraBottomPadding: 0 }
        : getAxisLabelStrategy({
            tickCount: visibleTickTexts.length,
            spacing,
            maxLabelLength,
            maxLabelWidth,
          })

  const densityRatio = spacing ? maxLabelWidth / spacing : 0

  if (!strategy.rotate && !strategy.truncateTo && !strategy.stagger) {
    if (options.labelShowFullOnHover === false) {
      visibleTickTexts.forEach(removeTickTitle)
    }
    return { tickCount: visibleTickTexts.length, densityRatio, rotate: 0, truncateTo: null }
  }

  visibleTickTexts.forEach((textNode, index) => {
    const fullText = textNode.textContent.trim()
    textNode.dataset.rawgraphsAxisOriginalLabel = fullText

    if (strategy.truncateTo) {
      textNode.textContent = truncateAxisLabel(fullText, strategy.truncateTo)
      if (options.labelShowFullOnHover !== false) {
        setTickTitle(textNode, fullText)
      } else {
        removeTickTitle(textNode)
      }
    } else if (options.labelShowFullOnHover) {
      setTickTitle(textNode, fullText)
    } else {
      removeTickTitle(textNode)
    }

    if (strategy.rotate) {
      const transform = textNode.getAttribute('transform') || ''
      if (!/rotate\(/.test(transform)) {
        textNode.setAttribute(
          'transform',
          `${transform} rotate(${strategy.rotate})`.trim()
        )
      }
      textNode.setAttribute('text-anchor', 'end')
      textNode.setAttribute('dx', '-0.2em')
      textNode.setAttribute('dy', '0.55em')
    } else if (strategy.stagger) {
      textNode.setAttribute('text-anchor', 'middle')
      textNode.setAttribute('dx', '0')
      const isLowerRow = index % 2 !== 0
      textNode.setAttribute('dy', isLowerRow ? '1.9em' : '0.8em')
      setTickLineLength(textNode, isLowerRow ? 16 : 8)
    }
  })

  ensureSvgBottomPadding(svgNode, strategy.extraBottomPadding)
  return {
    tickCount: visibleTickTexts.length,
    densityRatio,
    rotate: strategy.rotate,
    truncateTo: strategy.truncateTo,
    stagger: strategy.stagger ?? false,
  }
}

function getGroupTranslateX(node) {
  const transform = node.getAttribute('transform') || ''
  const match = transform.match(/translate\s*\(\s*([-\d.]+)/)
  return match ? parseFloat(match[1]) : 0
}

function getYAxisAvailableWidth(svgNode, axisGroup) {
  // Simple case: axis group positioned via translate (standard D3 pattern)
  const fromTransform = getGroupTranslateX(axisGroup)
  if (fromTransform > 10) return fromTransform

  // Rawgraphs case: group has no transform; axis line sits at explicit local x.
  // Use getCTM() for the full SVG-space group origin, then add the axis path x.
  let axisGroupSvgX = 0
  try {
    const pt = svgNode.createSVGPoint()
    const ctm = axisGroup.getCTM()
    if (ctm) axisGroupSvgX = pt.matrixTransform(ctm).x
  } catch {}

  let axisLineLocalX = 0
  const axisPath = axisGroup.querySelector('path.domain, path')
  if (axisPath) {
    const dAttr = axisPath.getAttribute('d') || ''
    const match = dAttr.match(/M\s*([-\d.]+)/)
    if (match) {
      axisLineLocalX = parseFloat(match[1])
    } else {
      try { axisLineLocalX = axisPath.getBBox().x } catch {}
    }
  }

  // Measure from the left edge of the background rect (negative x = chart has left margin)
  const bgRect = svgNode.querySelector('[data-lugsail-theme-background="true"]')
  const bgRectX = bgRect ? parseFloat(bgRect.getAttribute('x') || '0') : 0
  const svgLeftBoundary = Math.min(0, bgRectX)

  return axisGroupSvgX + axisLineLocalX - svgLeftBoundary
}

export function cleanYAxisLabels(svgNode, options = {}) {
  if (!svgNode) return null

  if (options.labelHandlingMode === 'manual') return null

  const axisGroup = getAxisGroupNode(svgNode, 'y', options.chartId)
  if (!axisGroup) return null

  const tickTexts = getTickTextNodes(axisGroup)
  if (tickTexts.length < 2) return null

  const availableWidth = getYAxisAvailableWidth(svgNode, axisGroup)
  if (availableWidth <= 10) return null

  const maxAllowedWidth = Math.max(40, availableWidth - 8)

  const maxLabelWidth = tickTexts.reduce((max, node) => {
    try {
      return Math.max(max, node.getBBox().width)
    } catch {
      return max
    }
  }, 0)

  if (maxLabelWidth <= maxAllowedWidth) {
    return { truncatedTo: null, tickCount: tickTexts.length, availableWidth }
  }

  const maxLabelLength = tickTexts.reduce(
    (max, node) => Math.max(max, node.textContent.trim().length),
    0
  )
  if (!maxLabelLength) return null

  const pixelsPerChar = maxLabelWidth / maxLabelLength
  const targetChars = Math.max(6, Math.floor(maxAllowedWidth / pixelsPerChar))

  if (targetChars >= maxLabelLength) {
    return { truncatedTo: null, tickCount: tickTexts.length, availableWidth }
  }

  tickTexts.forEach((textNode) => {
    const fullText = textNode.textContent.trim()
    if (fullText.length <= targetChars) return
    textNode.dataset.rawgraphsAxisOriginalLabel = fullText
    textNode.textContent = truncateAxisLabel(fullText, targetChars)
    if (options.labelShowFullOnHover !== false) {
      setTickTitle(textNode, fullText)
    }
  })

  return { truncatedTo: targetChars, tickCount: tickTexts.length, availableWidth }
}
